<script lang="ts">
    import { location } from '../../framework/js/spaLocation';
    import { push } from 'svelte-spa-router';
    import { api } from '../../framework/js/api.js';
    import { toast } from '../../framework/js/toast.js';
    import { spinner } from '../../framework/js/spinner.js';

    interface UserDetail {
        uuid: string;
        email: string;
        salutation?: string;
        first_name: string;
        last_name: string;
        phone?: string;
        credit_total?: number;
        onboarding_completed?: boolean;
        active_team_uuid?: string | null;
        user_role?: { uuid: string; name: string } | null;
        user_status?: { uuid: string; name: string } | null;
        country?: string | null;
    }

    interface UserTeam {
        uuid: string;
        name: string;
        role: string;
        status: string;
        is_active: boolean;
        joined_at: string | null;
        payment_plan: { uuid: string; name: string } | null;
        is_on_trial: boolean;
        trial_days_remaining: number | null;
        member_count: number;
    }

    let user: UserDetail | null = null;
    let teams: UserTeam[] = [];
    let isLoading = false;

    $: userUuid = ($location || '').split('/admin/users/')[1]?.split('/')[0] || '';

    $: if (userUuid) {
        loadUserDetail();
    }

    async function loadUserDetail() {
        isLoading = true;
        spinner.show();
        try {
            const response: any = await api.getAdminUserDetail(userUuid);
            if (response.success) {
                user = response.data?.user ?? null;
                teams = response.data?.teams ?? [];
            } else {
                toast.showNotification(false, response.message || 'Failed to load user');
                user = null;
                teams = [];
            }
        } catch (error) {
            toast.showNotification(false, 'Error loading user detail');
            console.error(error);
            user = null;
            teams = [];
        } finally {
            spinner.hide();
            isLoading = false;
        }
    }

    function fullName(u: UserDetail): string {
        return [u.first_name, u.last_name].filter(Boolean).join(' ') || '—';
    }

    function formatDate(dateString: string | null): string {
        if (!dateString) return '—';
        return new Date(dateString).toLocaleDateString(navigator.language, { dateStyle: 'medium' });
    }

    function viewTeam(teamUuid: string) {
        push(`/admin/teams/${teamUuid}`);
    }

    function handleTeamRowKeydown(event: KeyboardEvent, teamUuid: string) {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            viewTeam(teamUuid);
        }
    }

    function teamStatusLabel(team: UserTeam): string {
        if (team.is_on_trial) {
            return team.trial_days_remaining != null
                ? `Trial (${team.trial_days_remaining}d)`
                : 'Trial';
        }
        return team.payment_plan?.name ?? 'No Plan';
    }
</script>

<div class="admin-user-detail">
    <div class="back-nav">
        <button type="button" class="back-btn" on:click={() => push('/admin/users')}>
            <i class="icon arrow left" aria-hidden="true"></i> Back to Users
        </button>
    </div>

    {#if isLoading}
        <div class="loading-state">Loading user details...</div>
    {:else if !user}
        <div class="empty-state">
            <p>User not found.</p>
        </div>
    {:else}
        <div class="page-header">
            <div class="header-content">
                <h2 class="page-title">{fullName(user)}</h2>
                <p class="page-subtitle">{user.email}</p>
            </div>
        </div>

        <div class="info-grid">
            <div class="info-card">
                <div class="info-label">Role</div>
                <div class="info-value">{user.user_role?.name ?? '—'}</div>
            </div>
            <div class="info-card">
                <div class="info-label">Status</div>
                <div class="info-value">{user.user_status?.name ?? '—'}</div>
            </div>
            <div class="info-card">
                <div class="info-label">Phone</div>
                <div class="info-value">{user.phone || '—'}</div>
            </div>
            <div class="info-card">
                <div class="info-label">Tokens</div>
                <div class="info-value num-value">{user.credit_total ?? 0}</div>
            </div>
            <div class="info-card">
                <div class="info-label">Onboarding</div>
                <div class="info-value">{user.onboarding_completed ? 'Completed' : 'Pending'}</div>
            </div>
            {#if user.country}
                <div class="info-card">
                    <div class="info-label">Country</div>
                    <div class="info-value">{user.country}</div>
                </div>
            {/if}
        </div>

        <section class="teams-section">
            <div class="section-header">
                <h3>Teams</h3>
                <span class="count-badge">{teams.length} team{teams.length !== 1 ? 's' : ''}</span>
            </div>

            {#if teams.length === 0}
                <div class="empty-teams">
                    <p>This user is not a member of any teams.</p>
                </div>
            {:else}
                <div class="teams-table">
                    <table class="ui celled table">
                        <thead>
                            <tr>
                                <th>Team Name</th>
                                <th>Role</th>
                                <th>Membership</th>
                                <th>Plan</th>
                                <th class="center-col">Members</th>
                                <th>Joined</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {#each teams as team (team.uuid)}
                                <tr
                                    class="clickable-row"
                                    tabindex="0"
                                    on:click={() => viewTeam(team.uuid)}
                                    on:keydown={(e) => handleTeamRowKeydown(e, team.uuid)}
                                >
                                    <td class="team-name-cell">
                                        <span class="team-name">{team.name}</span>
                                        {#if team.is_active}
                                            <span class="active-tag">Active</span>
                                        {/if}
                                    </td>
                                    <td><span class="pill">{team.role}</span></td>
                                    <td><span class="pill">{team.status}</span></td>
                                    <td>{teamStatusLabel(team)}</td>
                                    <td class="center-col num-value">{team.member_count}</td>
                                    <td class="date-cell">{formatDate(team.joined_at)}</td>
                                    <td>
                                        <button
                                            type="button"
                                            class="view-button"
                                            on:click|stopPropagation={() => viewTeam(team.uuid)}
                                        >
                                            View
                                        </button>
                                    </td>
                                </tr>
                            {/each}
                        </tbody>
                    </table>
                </div>
            {/if}
        </section>
    {/if}
</div>

<style>
    .admin-user-detail {
        padding: var(--space-5) var(--space-6);
    }

    .back-nav {
        margin-bottom: var(--space-4);
    }

    .back-btn {
        display: inline-flex;
        align-items: center;
        gap: 0.35rem;
        padding: 0.5rem 0.75rem;
        border: none;
        background: transparent;
        color: var(--primary);
        cursor: pointer;
        font-size: 0.9rem;
        border-radius: 6px;
        transition: background-color 0.2s;
    }

    .back-btn:hover {
        background: color-mix(in srgb, var(--primary) 8%, transparent);
    }

    .back-btn:focus-visible {
        outline: none;
        box-shadow: var(--focus-ring);
    }

    .page-header {
        margin-bottom: var(--space-5);
    }

    .page-title {
        font-size: 2rem;
        font-weight: 500;
        color: var(--grey);
        margin-bottom: var(--space-2);
    }

    .page-subtitle {
        color: var(--grey-secondary);
        font-size: 1.125rem;
    }

    .info-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
        gap: var(--space-4);
        margin-bottom: var(--space-6);
    }

    .info-card {
        background: var(--white);
        border-radius: 12px;
        padding: var(--space-4);
        box-shadow: var(--shadow-sm);
    }

    .info-label {
        font-size: 0.8rem;
        color: var(--grey-secondary);
        margin-bottom: var(--space-2);
        text-transform: uppercase;
        letter-spacing: 0.03em;
    }

    .info-value {
        font-size: 1rem;
        font-weight: 500;
        color: var(--grey);
    }

    .num-value {
        font-variant-numeric: tabular-nums;
    }

    .teams-section {
        background: var(--white);
        border-radius: 12px;
        box-shadow: var(--shadow-sm);
        overflow: hidden;
    }

    .section-header {
        display: flex;
        align-items: center;
        gap: var(--space-3);
        padding: var(--space-4) var(--space-5);
        border-bottom: 1px solid var(--border-color);
    }

    .section-header h3 {
        margin: 0;
        font-size: 1.25rem;
        font-weight: 500;
        color: var(--grey);
    }

    .count-badge {
        background: var(--primary);
        color: var(--white);
        padding: 0.25rem 0.75rem;
        border-radius: 9999px;
        font-size: 0.75rem;
        font-weight: 500;
    }

    .empty-teams {
        padding: 3rem var(--space-5);
        text-align: center;
        color: var(--grey-secondary);
    }

    .teams-table table {
        margin: 0;
    }

    .clickable-row {
        cursor: pointer;
        transition: background-color 0.2s;
    }

    .clickable-row:hover,
    .clickable-row:focus-visible {
        background: var(--greyish);
        outline: none;
    }

    .team-name-cell {
        min-width: 180px;
    }

    .team-name {
        font-weight: 500;
        color: var(--grey);
    }

    .active-tag {
        display: inline-block;
        margin-left: var(--space-2);
        padding: 0.15rem 0.5rem;
        border-radius: 9999px;
        font-size: 0.7rem;
        font-weight: 500;
        background: var(--status-success-soft);
        color: var(--status-success-ink);
    }

    .pill {
        display: inline-flex;
        padding: 0.2rem 0.6rem;
        border-radius: 9999px;
        font-size: 0.75rem;
        font-weight: 500;
        background: var(--greyish);
        color: var(--grey);
        text-transform: capitalize;
    }

    .center-col {
        text-align: center;
    }

    .date-cell {
        white-space: nowrap;
        font-size: 0.875rem;
        color: var(--grey-secondary);
    }

    .view-button {
        padding: 0.35rem 0.75rem;
        font-size: 0.8rem;
        font-weight: 500;
        border-radius: 6px;
        border: 1px solid var(--border-color);
        background: transparent;
        color: var(--primary);
        cursor: pointer;
        transition: border-color 0.2s, background-color 0.2s;
    }

    .view-button:hover {
        border-color: var(--primary);
        background: color-mix(in srgb, var(--primary) 5%, transparent);
    }

    .loading-state,
    .empty-state {
        text-align: center;
        padding: 4rem 2rem;
        color: var(--grey-secondary);
        background: var(--white);
        border-radius: 12px;
        box-shadow: var(--shadow-sm);
    }

    @media (max-width: 768px) {
        .admin-user-detail {
            padding: var(--space-4);
        }

        .teams-table {
            overflow-x: auto;
        }
    }
</style>
